import React, { memo, useEffect, useState } from "react";
import { Messages } from "./Messages";
import CloseIcon from "@material-ui/icons/Close";
import { Lightbox } from "react-modal-image";
import * as S from "./styles";

import { ContactDetails } from "../ContactDetails";
import { useTranslation } from "react-i18next";
import FavoriteAction from "../ContactDetails/FavoriteAction/FavoriteAction";
import { ReOpen } from "../ContactDetails/ReOpen/ReOpen";
import AfterRegisterSoldProduct from "../ContactDetails/SoldProducts/AfterRegisterSoldProduct";

import { useContacts, useContactsState } from "../../contexts";
import { isFutureDate } from "modules/app/utils";
import SnoozedBar from "./SnoozedBar/SnoozedBar";
import InfoBar from "./InfoBar/InfoBar";
import { RepliedMessage } from "./RepliedMessage";
import { toast } from "react-toastify";
import { useParams } from "react-router-dom";
import * as http from "modules/app/utils/http";

import { PasteImageModal } from "./Messages/PasteImageModal";
import { pasteImgListener } from "modules/app/utils/models/pasteImgListener";
import SendMessage from "./SendMessage/SendMessage";

export default memo(function Chat() {
  const { updateCurrentContact, setSpotlight } = useContacts();
  const { currentContact } = useContactsState();
  const { id } = useParams();
  const { t } = useTranslation(["Chat", "Customer"]);

  const [showSideBar, setShowSideBar] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [lastSendedMessage, setLastSendedMessage] = useState({
    message: null,
    contactId: null,
  });
  const [replySources, setReplySources] = useState();
  const [pasteImgModal, setPasteImgModal] = useState(false);
  const [imgSrc, setImgSrc] = useState();
  const [imgBlob, setImgBlob] = useState();
  const [answerMessage, setAnswerMessage] = useState();
  const [lightBoxVisible, setLightBoxVisible] = useState(false);
  const [lastMsgVisible, setLastMsgVisible] = useState(false);

  async function checkEmptiesReplySources() {
    let emptyReplySources = true;

    Object.keys(replySources).forEach((key) => {
      if (replySources[key]?.checked) {
        emptyReplySources = false;
      }
    });

    if (!emptyReplySources) {
      return replySources;
    }

    const newReplySources = {};

    Object.keys(replySources).forEach((key) => {
      newReplySources[key] = {
        name: key,
        checked: true,
      };
    });

    setReplySources(newReplySources);
    http.updateReplySources(id, newReplySources);

    return newReplySources;
  }

  async function sendMessage(text) {
    const currentReplySources = await checkEmptiesReplySources();
    const choosedContact = currentContact.id;

    if (text && text.trim() && !isSending) {
      setIsSending(true);

      Object.keys(currentReplySources).forEach((key) => {
        const checked = currentReplySources[key]?.checked;
        const name = currentReplySources[key]?.name;

        if (checked) {
          http
            .createMessage(choosedContact, {
              message: {
                body: text,
                message_quoted_id: answerMessage?.id,
              },
              reply_sources: { [key]: { name, checked } },
            })
            .then((result) => {
              setLastSendedMessage({
                message: { ...result.data, from_api: true },
                contactId: choosedContact,
              });
              setAnswerMessage(null);
              setLastMsgVisible(true);
            })
            .catch(() => {
              toast.error(t("Chat:labels.send_msg_error"));
            })
            .finally(() => setIsSending(false));
        }
      });
    }
  }

  function handleSubmit(text) {
    sendMessage(text);
  }

  useEffect(() => {
    setLastMsgVisible(false);
  }, [lastSendedMessage]);

  function handlePasteImg(e) {
    const results = pasteImgListener(e);
    if (results) {
      setImgSrc(results?.imgSrc);
      setImgBlob(results?.blob);
      setPasteImgModal(true);
    }
  }

  useEffect(() => {
    function updateLastMessage() {
      if (lastSendedMessage.contactId === currentContact.id) {
        updateCurrentContact({
          ...currentContact,
          messages: [...currentContact.messages, lastSendedMessage.message],
        });
      }
    }

    if (lastSendedMessage.contactId !== null) {
      updateLastMessage();
    }
  }, [lastSendedMessage]);

  useEffect(() => {
    setShowSideBar(true);
    setSpotlight();
  }, [id]);

  if (!currentContact) return <></>;

  return (
    <S.Main>
      {!!lightBoxVisible && (
        <Lightbox
          large={currentContact.contact.avatar_url}
          alt={`Foto de perfil de ${currentContact.contact.name}`}
          onClose={() => {
            setLightBoxVisible(false);
          }}
          hideDownload={true}
          hideZoom={true}
        />
      )}

      <S.Container variant="outlined">
        <S.ContactBar>
          <S.ContactAvatar
            src={currentContact?.contact?.avatar_url}
            onClick={() => {
              if (currentContact.contact.avatar_url) {
                setLightBoxVisible(true);
              } else {
                toast.info(t("Chat:labels.avatarUnavailable"));
              }
            }}
          />

          <S.ContactInfo onClick={() => setShowSideBar(!showSideBar)}>
            <S.InfoSection>
              {currentContact.name === undefined ? (
                <S.InfoSectionLoading>
                  {t("Chat:labels.loading")}
                </S.InfoSectionLoading>
              ) : (
                <S.CurrentContactInfo>
                  <S.ContactName>{currentContact.name}</S.ContactName>
                  {!!currentContact?.contact?.phone && (
                    <>
                      {!!currentContact.valid_whatsapp && (
                        <S.WhatsappStatus className="available">
                          {t("Chat:labels.whatsappAvailable")}
                        </S.WhatsappStatus>
                      )}

                      {!currentContact.valid_whatsapp && (
                        <S.WhatsappStatus className="unavailable">
                          {t("Chat:labels.whatsappUnavailable")}
                        </S.WhatsappStatus>
                      )}
                    </>
                  )}
                </S.CurrentContactInfo>
              )}
            </S.InfoSection>
            <S.ContactInfoIcon />
          </S.ContactInfo>
          <FavoriteAction />
        </S.ContactBar>

        {currentContact && currentContact?.status == "filed" && (
          <S.StatusBar>
            <S.ArchivedContact>
              <S.ArchiveIcon />
              {t("Customer:details.reOpen.filed")}
              <ReOpen>
                <S.ReOpenButtonTitle>
                  {t("Customer:details.reOpen.action")}
                </S.ReOpenButtonTitle>
              </ReOpen>
            </S.ArchivedContact>
          </S.StatusBar>
        )}

        {currentContact &&
          currentContact?.status == "open" &&
          isFutureDate(currentContact?.last_activity?.date) && (
            <SnoozedBar customer={currentContact} />
          )}

        {currentContact &&
          currentContact?.status != "filed" &&
          !isFutureDate(currentContact?.last_activity?.date) && (
            <InfoBar customer={currentContact} />
          )}

        <Messages
          customer={currentContact}
          setAnswerMessage={setAnswerMessage}
          upMessage={!!answerMessage}
          isLastMsgVisible={lastMsgVisible}
        />

        <S.ResponseContainer>
          <S.AnimatedContent
            className={`animated ${!!answerMessage && "active"} `}
          >
            <RepliedMessage
              message={answerMessage || {}}
              customer={currentContact || {}}
            />

            <S.CloseButton icon="close" onClick={() => setAnswerMessage(null)}>
              <CloseIcon />
            </S.CloseButton>
          </S.AnimatedContent>
        </S.ResponseContainer>

        {currentContact && (
          <SendMessage
            replySources={replySources}
            setReplySources={setReplySources}
            answerMessage={answerMessage}
            setAnswerMessage={setAnswerMessage}
            handleSubmit={handleSubmit}
            handlePasteImg={handlePasteImg}
            isSending={isSending}
            setIsSending={setIsSending}
          />
        )}
      </S.Container>

      {showSideBar && currentContact && (
        <>
          <AfterRegisterSoldProduct />

          <ContactDetails setShowSidebar={setShowSideBar} />
        </>
      )}

      {pasteImgModal && (
        <PasteImageModal
          imgSrc={imgSrc}
          handleCloseModal={() => setPasteImgModal(false)}
          blob={imgBlob}
        />
      )}
    </S.Main>
  );
});
